import React, { useEffect, useState, useRef, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import {
  Bell, Check, Heart, MessageCircle, UserPlus, UserCheck, ShieldCheck,
  X, MessageSquare, Volume2, VolumeX,
} from "lucide-react";
import { api } from "@/lib/api";
import { notificationHref } from "@/lib/notificationsRouting";
import { playChime, unlockAudio, isMuted, setMuted, isQuietHour } from "@/lib/notificationSound";
import {
  pushSupported, getPushStatus, enableWebPush, disableWebPush, attachSWMessageRouter,
} from "@/lib/webPush";

const ICONS = {
  like: Heart,
  comment: MessageCircle,
  friend_request: UserPlus,
  friend_accepted: UserCheck,
  application_approved: ShieldCheck,
  application_rejected: X,
  dm: MessageSquare,
};

function relativeTime(iso) {
  if (!iso) return "";
  const ms = Date.now() - new Date(iso).getTime();
  const min = Math.floor(ms / 60000);
  if (min < 1) return "just now";
  if (min < 60) return `${min}m`;
  const h = Math.floor(min / 60);
  if (h < 24) return `${h}h`;
  return `${Math.floor(h / 24)}d`;
}

export default function NotificationsBell() {
  const [open, setOpen] = useState(false);
  const [items, setItems] = useState([]);
  const [unread, setUnread] = useState(0);
  const [muted, setMutedState] = useState(isMuted());
  const [pushState, setPushState] = useState({ supported: false, subscribed: false, permission: "default" });
  const [pushBusy, setPushBusy] = useState(false);
  const [pushErr, setPushErr] = useState("");
  const prevUnreadRef = useRef(null); // null until first poll establishes baseline
  const ref = useRef(null);
  const nav = useNavigate();

  const refresh = useCallback(async () => {
    try {
      const [list, count] = await Promise.all([
        api.get("/notifications"),
        api.get("/notifications/unread-count"),
      ]);
      setItems(list.data);
      const next = count.data.count;
      // Play chime only when unread count strictly INCREASES after a baseline
      // is established (so initial page load is silent).
      if (prevUnreadRef.current !== null && next > prevUnreadRef.current) {
        playChime();
      }
      prevUnreadRef.current = next;
      setUnread(next);
    } catch (e) {
      /* ignore */
    }
  }, []);

  useEffect(() => {
    refresh();
    const t = setInterval(refresh, 15000);
    return () => clearInterval(t);
  }, [refresh]);

  // Unlock the Web Audio context on the first user gesture anywhere in the page,
  // so the chime can play even if the user has never opened the bell.
  useEffect(() => {
    let done = false;
    const fn = () => {
      if (done) return;
      done = true;
      unlockAudio();
      window.removeEventListener("pointerdown", fn, true);
      window.removeEventListener("keydown", fn, true);
    };
    window.addEventListener("pointerdown", fn, true);
    window.addEventListener("keydown", fn, true);
    return () => {
      window.removeEventListener("pointerdown", fn, true);
      window.removeEventListener("keydown", fn, true);
    };
  }, []);

  // Bootstrap push status once on mount, and listen for SW navigation messages
  // (used when a notification is clicked while the app is open).
  useEffect(() => {
    if (!pushSupported()) return undefined;
    getPushStatus().then(setPushState).catch(() => {});
    const off = attachSWMessageRouter((path) => nav(path));
    return () => off?.();
  }, [nav]);

  const togglePush = async (e) => {
    e.stopPropagation();
    setPushErr("");
    setPushBusy(true);
    try {
      if (pushState.subscribed) {
        await disableWebPush();
        setPushState({ ...pushState, subscribed: false });
      } else {
        const r = await enableWebPush();
        setPushState({ supported: true, subscribed: r.subscribed, permission: r.permission });
      }
    } catch (err) {
      setPushErr(err.message || "Could not toggle push notifications.");
    } finally {
      setPushBusy(false);
    }
  };

  // Click outside to close
  useEffect(() => {
    if (!open) return undefined;
    const fn = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", fn);
    return () => document.removeEventListener("mousedown", fn);
  }, [open]);

  const toggle = () => {
    // First user gesture unlocks the audio context (Safari requirement).
    unlockAudio();
    setOpen((v) => !v);
  };

  const toggleMute = (e) => {
    e.stopPropagation();
    const next = !muted;
    setMuted(next);
    setMutedState(next);
    if (!next) {
      // Confirm un-muting with a small chime so the user knows it's active.
      unlockAudio();
      playChime();
    }
  };

  const markAllRead = async () => {
    try {
      await api.post("/notifications/read-all");
      setUnread(0);
      setItems((arr) => arr.map((n) => ({ ...n, read: true })));
    } catch (e) {
      /* ignore */
    }
  };

  const handleClick = async (n) => {
    setOpen(false);
    if (!n.read) {
      // Optimistic mark-as-read; backend call is fire-and-forget.
      setItems((arr) => arr.map((x) => (x.id === n.id ? { ...x, read: true } : x)));
      setUnread((c) => Math.max(0, c - 1));
      api.post(`/notifications/${n.id}/read`).catch(() => {});
    }
    nav(notificationHref(n));
  };

  return (
    <div className="relative" ref={ref}>
      <button
        onClick={toggle}
        className="relative p-2 text-white/70 hover:text-[#d4af37]"
        data-testid="notifications-bell"
        aria-label="Notifications"
      >
        <Bell size={18} strokeWidth={1.5} />
        {unread > 0 && (
          <span
            className="absolute -top-0.5 -right-0.5 min-w-[18px] h-[18px] rounded-full bg-[#d4af37] text-black text-[10px] font-semibold flex items-center justify-center px-1"
            data-testid="notifications-badge"
          >
            {unread > 9 ? "9+" : unread}
          </span>
        )}
      </button>

      {open && (
        <div
          className="absolute right-0 top-12 w-80 sm:w-96 bg-[#0a0a0a] border border-white/10 rounded-md shadow-2xl z-50 max-h-[70vh] overflow-y-auto"
          data-testid="notifications-panel"
        >
          <div className="px-4 py-3 border-b border-white/10 flex items-center justify-between bg-[#0a0a0a]">
            <span className="text-xs uppercase tracking-[0.25em] text-[#d4af37]">Notifications</span>
            <div className="flex items-center gap-3">
              <button
                type="button"
                onClick={(e) => { e.stopPropagation(); unlockAudio(); playChime(); }}
                className="text-white/40 hover:text-[#d4af37] text-[10px] uppercase tracking-widest"
                data-testid="notifications-test-sound"
                title="Test chime"
              >
                Test
              </button>
              <button
                type="button"
                onClick={toggleMute}
                className="text-white/50 hover:text-[#d4af37] transition"
                data-testid="notifications-mute-toggle"
                aria-label={muted ? "Unmute notifications" : "Mute notifications"}
                title={muted ? "Sound: off" : isQuietHour() ? "Sound: silenced (quiet hours)" : "Sound: on"}
              >
                {muted ? <VolumeX size={14} strokeWidth={1.5} /> : <Volume2 size={14} strokeWidth={1.5} />}
              </button>
              {items.some((n) => !n.read) && (
                <button
                  type="button"
                  onClick={markAllRead}
                  className="text-[10px] uppercase tracking-widest text-white/50 hover:text-[#d4af37] transition"
                  data-testid="notifications-mark-all-read"
                >
                  Mark all read
                </button>
              )}
            </div>
          </div>
          {isQuietHour() && !muted && (
            <div className="px-4 py-2 bg-[#141414] border-b border-white/10 text-[10px] uppercase tracking-widest text-white/40" data-testid="notifications-dnd-banner">
              Quiet hours active · no chime
            </div>
          )}
          {pushState.supported && (
            <div
              className="px-4 py-2 border-b border-white/10 bg-[#141414]/60 flex items-center justify-between gap-3"
              data-testid="notifications-push-row"
            >
              <div className="min-w-0">
                <div className="text-[10px] uppercase tracking-[0.2em] text-white/60">
                  Push notifications
                </div>
                <div className="text-[10px] text-white/40 mt-0.5">
                  {pushState.permission === "denied"
                    ? "Blocked in browser settings"
                    : pushState.subscribed
                    ? "Active — alerts even when app is closed"
                    : "Off — turn on to receive alerts when away"}
                </div>
              </div>
              <button
                type="button"
                onClick={togglePush}
                disabled={pushBusy || pushState.permission === "denied"}
                className={`shrink-0 px-3 py-1 text-[10px] uppercase tracking-widest border transition ${
                  pushState.subscribed
                    ? "border-emerald-400/40 text-emerald-300 hover:border-red-400/40 hover:text-red-300"
                    : "border-[#d4af37]/50 text-[#d4af37] hover:bg-[#d4af37] hover:text-black"
                } disabled:opacity-40`}
                data-testid="notifications-push-toggle"
              >
                {pushBusy ? "…" : pushState.subscribed ? "On" : "Enable"}
              </button>
            </div>
          )}
          {pushErr && (
            <div className="px-4 py-2 bg-red-950/30 border-b border-red-500/30 text-[11px] text-red-200" data-testid="notifications-push-error">
              {pushErr}
            </div>
          )}
          {items.length === 0 ? (
            <div className="p-8 text-center text-white/40 text-sm" data-testid="notifications-empty">
              You're all caught up.
            </div>
          ) : (
            <div className="divide-y divide-white/5">
              {items.map((n) => {
                const Icon = ICONS[n.type] || Bell;
                return (
                  <button
                    key={n.id}
                    type="button"
                    onClick={() => handleClick(n)}
                    className={`w-full text-left px-4 py-3 flex gap-3 hover:bg-white/[0.03] transition ${n.read ? "opacity-60" : ""}`}
                    data-testid={`notification-item-${n.id}`}
                  >
                    <div className="w-8 h-8 rounded-full bg-[#1a1a1a] border border-white/10 flex items-center justify-center shrink-0">
                      <Icon size={14} strokeWidth={1.5} className="text-[#d4af37]" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="text-sm text-white/90 leading-snug">{n.text}</div>
                      <div className="text-[10px] uppercase tracking-widest text-white/40 mt-1">
                        {relativeTime(n.created_at)} ago
                      </div>
                    </div>
                    {!n.read && <span className="w-1.5 h-1.5 rounded-full bg-[#d4af37] mt-2 shrink-0" />}
                  </button>
                );
              })}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
